import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BatteryFullIcon, BuildingIcon, CheckIcon, RefreshCwIcon, TruckIcon } from 'lucide-react';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
import { offers } from '../data/product';
import { RX_INSERT_UPDATES_URL, RX_INSERT_FALLBACK_MAILTO } from '../data/rxInsert';
import { CtaButton } from './CtaButton';
import { LifetimeSeal } from './LifetimeSeal';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { trackCustom, packEvents } from '../utils/tracking';
import { requestOffer, PackId } from '../utils/offerBus';
interface ObjectionRecoveryProps {
  /** The objection the visitor picked. Nothing renders until they pick one. */
  active: ObjectionId | null;
  /** Reopens the selector. */
  onChooseAnother: () => void;
}
const headlineClass = 'grit-headline text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase leading-[0.92] text-offwhite';
const subClass = 'mt-5 max-w-2xl text-[15px] leading-relaxed text-steel-300 sm:text-lg';
const statementClass = 'mt-6 border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl';
function Bullets({
  items
}: {
  items: string[];
}) {
  return <ul className="mt-6 grid gap-2.5" data-id="element-394" data-mp="29xbocb5dxf1f">
      {items.map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-395" data-mp="2xll7f5b7roov">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-396" data-mp="32lr10r11nl9l" />
          {line}
        </li>)}
    </ul>;
}
const scrollToCompany = () => {
  document.getElementById('company')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};

/** The public packs only. Private pricing never appears here. */
function PackClose({
  recommended,
  cta
}: {
  recommended: PackId;
  cta: string;
}) {
  return <div className="mt-10 border-t border-white/10 pt-8" data-id="element-397" data-mp="3lch52j2cpe41">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-398" data-mp="3v7fnlw35u5a1">Recommended for you</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-399" data-mp="3q834nmfeticq">
        {offers.map(offer => {
        const isPick = offer.id === recommended;
        return <li key={offer.id} data-id="element-400" data-mp="3m93xrwa4bbin">
              <a href="#offer" data-buy-intent="true" onClick={() => {
            requestOffer(offer.id);
            trackCustom(packEvents[offer.id].selected);
            trackCustom('mini_funnel_cta_clicked', {
              pack: offer.id
            });
          }} className={`flex h-full flex-col border p-4 transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${isPick ? 'border-grit-500 bg-grit-500/10 hover:bg-grit-500/15' : 'border-white/15 bg-ink-900 hover:border-white/35'}`} data-id="element-401" data-mp="3h8fyknbqjodc">
                <span className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-402" data-mp="1416u5oqtmg24">
                  {offer.eyebrow ?? offer.kicker}
                </span>
                <span className="font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-403" data-mp="192e49ugfrrby">{offer.units}</span>
                <span className="mt-2 font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-404" data-mp="1i0psxqxqsf1r">{offer.price}</span>
                <span className="mt-1 text-[12px] font-semibold text-steel-400" data-id="element-405" data-mp="lbi6mgpau1ws">
                  {offer.perUnit ?? 'One-year coverage · lifetime available'}
                </span>
                <span className="mt-auto pt-3 text-[12px] font-semibold text-steel-400" data-id="element-406" data-mp="ubnaigvaai67">
                  {offer.freeShipping ? 'Free standard shipping' : 'Shipping at checkout, typically around $6.95'}
                </span>
              </a>
            </li>;
      })}
      </ul>
      <CtaButton offer={recommended} className="mt-6 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-407" data-mp="6lsyg1isgxn0">
        {cta}
      </CtaButton>
    </div>;
}
function PriceFunnel() {
  const splitRef = useTrackOnView<HTMLDivElement>('price_split_offer_viewed');
  return <>
      <h2 className={headlineClass} data-id="element-408" data-mp="3tceufk0wccap">
        Here&apos;s what
        <br data-id="element-409" data-mp="3tdptx7nb6xw0" />
        <span className="text-grit-500" data-id="element-410" data-mp="3tehrup4dp7pg">$49.95 gets you.</span>
      </h2>
      <p className={subClass} data-id="element-411" data-mp="3ok3s6aiooeez">
        Not a pair of glasses. A complete work system: light, three lenses and everything to carry and care for it.
      </p>
      <div className="mt-8 grid gap-8 lg:grid-cols-2" data-id="element-412" data-mp="18ul40mzdp6a">
        <Bullets items={['GritVue X1 frame with built-in 800-lumen light', '3 interchangeable lenses: Clear, Amber, Smoke', 'Hard protective case, storage pouch and cleaning cloth', 'GritVue anti-fog lens spray and USB charging cable', 'One-year replacement coverage, or Lifetime on multi-packs']} data-id="element-413" data-mp="18clqv50uuu5" />
        <div ref={splitRef} className="border border-white/12 bg-ink-900 p-6" data-id="element-414" data-mp="2czq0mfiyy6hq">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-415" data-mp="sh5r2y8io79t">Cost per X1</p>
          <dl className="mt-4 grid gap-2.5 text-[15px]" data-id="element-416" data-mp="xdw41cp0ymr0">
            {[['1 X1', '$49.95'], ['3 X1 · Buy 2, get 1 free', '$33.30 each'], ['6 X1 · Buy 4, get 2 free', '$33.30 each']].map(([label, value]) => <div key={label} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-2" data-id="element-417" data-mp="9p3fw2en9j6y">
                <dt className="text-steel-300" data-id="element-418" data-mp="drghcemcyuxx">{label}</dt>
                <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-419" data-mp="dovi7xdg2t3v">{value}</dd>
              </div>)}
          </dl>
          <p className="mt-4 text-[14px] leading-relaxed text-steel-300" data-id="element-420" data-mp="4rl48498spv9">
            Work with a crew? Split a 3-pack with two coworkers and each of you pays about $33.30, with lifetime coverage
            on all three.
          </p>
        </div>
      </div>
      <CtaButton offer="trio" className="mt-8 w-full sm:w-auto" onClick={() => {
      trackCustom('price_split_clicked');
      trackCustom('mini_funnel_cta_clicked');
    }} data-id="element-421" data-mp="1xndwr13szbw8">
        See the best value for me
      </CtaButton>
    </>;
}
function PrescriptionFunnel() {
  const updatesHref = RX_INSERT_UPDATES_URL || RX_INSERT_FALLBACK_MAILTO;
  return <>
      <h2 className={headlineClass} data-id="element-422" data-mp="2gdlx5bg4bo8q">
        Wear prescription lenses?
        <br data-id="element-423" data-mp="2gfy9n933x6ee" />
        <span className="text-grit-500" data-id="element-424" data-mp="2beshbcdczl5e">Your X1 is prescription ready.</span>
      </h2>
      <p className={subClass} data-id="element-425" data-mp="2bgvecgcxiejm">
        Take your X1 to your trusted optical professional and ask about a custom prescription setup. You can buy and
        use it today.
      </p>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-426" data-mp="21ofwrk5jnrz9">
        <div className="border-2 border-grit-500 bg-grit-500/10 p-6" data-id="element-427" data-mp="2z4wmvpa3xuz1">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-428" data-mp="2z67kt8gvg8i8">Available now</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-429" data-mp="32majnhv8ftv0">Prescription ready</p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-430" data-mp="36nck220vybyk">Your vision. Your prescription. Your X1.</p>
        </div>
        <div className="border border-white/12 bg-ink-900 p-6" data-id="element-431" data-mp="3bm79hmtcyyrn">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-432" data-mp="3gmlboo8a3ehr">In development</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-433" data-mp="3lly922ws03yl">GritVue X1 RX Insert™</p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-434" data-mp="3plfbsoktin02">
            A removable prescription insert designed to sit behind the main lens, so your prescription stays in place while
            you switch Clear, Amber and Smoke. Prototype in development. Not required to use your X1.
          </p>
          <a href={updatesHref} onClick={() => trackCustom('rx_insert_updates_clicked')} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-435" data-mp="8839h350qt62">
            Get RX Insert updates
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-436" data-mp="tjjjhhjej7mu" />
          </a>
        </div>
      </div>
      <p className="mt-3 text-[12px] text-steel-500" data-id="element-437" data-mp="12hvk8cdzflt0">
        <Link to="/legal/terms" className="underline" data-id="element-438" data-mp="12j6la4g1mfgf">
          Terms apply.
        </Link>
      </p>
      <CtaButton offer="single" className="mt-8 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-439" data-mp="esu74bu7fmhe">
        Get my X1
      </CtaButton>
    </>;
}
function BatteryFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-440" data-mp="ok6zr5ntugoh">
        Light that lasts
        <br data-id="element-441" data-mp="jqchm6nve9ri" />
        <span className="text-grit-500" data-id="element-442" data-mp="jqm0zbeetio8">the whole job.</span>
      </h2>
      <p className={subClass} data-id="element-443" data-mp="3s6ffjkp6kk2n">
        One X1 runs up to 14 hours on low and up to 5 hours on high, and recharges over USB-C in about 90 minutes.
      </p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-444" data-mp="3n61eimlzyae4">
        {[{
        label: 'Low',
        value: 'Up to 14 hours'
      }, {
        label: 'High',
        value: 'Up to 5 hours'
      }].map(mode => <div key={mode.label} className="flex items-center gap-4 border border-white/12 bg-ink-900 px-5 py-5" data-id="element-445" data-mp="27imro6crttvc">
            <BatteryFullIcon className="h-8 w-8 shrink-0 text-grit-500" aria-hidden="true" data-id="element-446" data-mp="1yohuz6ayho7w" />
            <div data-id="element-447" data-mp="1ym4479nb6y65">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-448" data-mp="2mec87oz6teso">{mode.label}</p>
              <p className="font-display text-2xl font-bold uppercase text-offwhite" data-id="element-449" data-mp="2qg60b8vbhah8">{mode.value}</p>
            </div>
          </div>)}
      </div>
      <p className={statementClass} data-id="element-450" data-mp="2chzbsjc9cmu8">
        Running high all shift?
        <br data-id="element-451" data-mp="2hdof8xreenvt" />
        Keep a second X1 charged and swap.
      </p>
      <p className="mt-3 text-[12px] text-steel-500" data-id="element-452" data-mp="1106p7m851ayy">Runtime varies by light mode, battery age, temperature and use.</p>
    </>;
}
function FitFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-453" data-mp="15xgdgyjsjgl7">
        You&apos;ll forget
        <br data-id="element-454" data-mp="3g82gpi09lg1n" />
        <span className="text-grit-500" data-id="element-455" data-mp="3g9nctc28971m">you&apos;re wearing it.</span>
      </h2>
      <p className={subClass} data-id="element-456" data-mp="3l63zs2xoxfd4">The light is built into the frame, so there is nothing strapped around your head.</p>
      <Bullets items={['1.4 oz (40 g), lighter than most safety glasses', 'No head strap, no battery pack on your neck', 'Wave-on motion sensor. No button to hunt for with gloves on']} data-id="element-457" data-mp="36hhbvctzzfaa" />
    </>;
}
function ShippingFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-458" data-mp="31gjswy15yh7q">
        Shipping,
        <br data-id="element-459" data-mp="2njofktdvq3lv" />
        <span className="text-grit-500" data-id="element-460" data-mp="2nk6mql22y28k">straight answers.</span>
      </h2>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-461" data-mp="2shgck1tdsfwx">
        <div className="border border-white/12 bg-ink-900 p-6" data-id="element-462" data-mp="2sk2hdtow0hbg">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-463" data-mp="lstk62nl46fo">Single X1</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-464" data-mp="qpa9h06isquc">Calculated at checkout</p>
          <p className="mt-2 text-[15px] text-steel-300" data-id="element-465" data-mp="2b6je2ebsjc9k">Standard shipping is typically around $6.95.</p>
        </div>
        <div className="border-2 border-grit-500 bg-grit-500/10 p-6" data-id="element-466" data-mp="2kygr7qmd0eax">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-467" data-mp="2kzrttn42jg0g">
            <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-468" data-mp="2g5nnn62mnc0f" />
            Multi-packs
          </p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-469" data-mp="2u182nqf8ja2k">Free standard shipping</p>
          <p className="mt-2 text-[15px] text-steel-300" data-id="element-470" data-mp="33x1ef7l4qzja">Every order of $99.90 or more ships free.</p>
        </div>
      </div>
      <p className="mt-4 text-[14px] text-steel-400" data-id="element-471" data-mp="2yz9o2i04vlh7">
        USPS, insured and tracked, from the U.S. Orders placed before 2pm CT ship the same business day.
      </p>
      <CtaButton offer="trio" className="mt-8 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-472" data-mp="14kgsuk35wwps">
        See free-shipping options
      </CtaButton>
    </>;
}
function WarrantyFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-473" data-mp="1yazdc3s846za">
        Break it.
        <br data-id="element-474" data-mp="2390ycu97mgde" />
        <span className="text-grit-500" data-id="element-475" data-mp="239ar2pln31ms">We replace it.</span>
      </h2>
      <div className="mt-8 flex flex-col items-start gap-6 border border-grit-500/50 bg-ink-900 p-6 sm:flex-row sm:items-center sm:p-8" data-id="element-476" data-mp="23bdo3tl7lv10">
        <LifetimeSeal className="h-24 w-24 shrink-0" data-id="element-477" data-mp="2d8086g5poi1z" />
        <dl className="grid flex-1 gap-3 text-[15px]" data-id="element-478" data-mp="2d5492oi4nnby">
          <div data-id="element-479" data-mp="2h7qua6kama06">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-480" data-mp="2h790rhp0m9vy">Single X1</dt>
            <dd className="text-steel-300" data-id="element-481" data-mp="2m0tswt3xg9n8">One-year replacement coverage, or add the Lifetime Replacement Warranty.</dd>
          </div>
          <div data-id="element-482" data-mp="2r0g28rgmx5qr">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-483" data-mp="2vxydwpm1dz2g">Every multi-pack</dt>
            <dd className="text-steel-300" data-id="element-484" data-mp="30wjdcmtgsepu">Lifetime Replacement Warranty on every X1 in the pack.</dd>
          </div>
          <div data-id="element-485" data-mp="p9pn159is36w">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-486" data-mp="pa7gju4ss3b4">Making a claim</dt>
            <dd className="text-steel-300" data-id="element-487" data-mp="k9juznzc8eoa">
              One email with a photo. The replacement unit is free; a flat $6.95 shipping and handling fee applies per
              approved replacement claim.
            </dd>
          </div>
        </dl>
      </div>
      <Link to="/legal/guarantee" className="mt-4 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-488" data-mp="17znt5ftrbm7w">
        View warranty terms
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-489" data-mp="y8t8uh02mwp3" />
      </Link>
    </>;
}
const recommendations: {
  label: string;
  body: string;
  pack: PackId | 'business';
  result: string;
}[] = [{
  label: 'Just for me',
  body: 'One X1 for your own work.',
  pack: 'single',
  result: '1 X1 · $49.95'
}, {
  label: 'One for work + backups',
  body: 'A spare charged and a spare in the truck.',
  pack: 'trio',
  result: '3 X1 · $99.90'
}, {
  label: 'Small crew',
  body: 'Outfit a few people at once.',
  pack: 'crew',
  result: '6 X1 · $199.80'
}, {
  label: 'Business / team',
  body: 'One order for the whole crew.',
  pack: 'business',
  result: '30 X1 · $999.00'
}];
function PackRecommender() {
  return <>
      <h2 className={headlineClass} data-id="element-490" data-mp="11jdq6w5pg78i">
        Who is it
        <br data-id="element-491" data-mp="1p8gfmq6kz4dk" />
        <span className="text-grit-500" data-id="element-492" data-mp="1p7n7plvvy8tu">for?</span>
      </h2>
      <p className={subClass} data-id="element-493" data-mp="1pa9li39ddkki">Pick the one that sounds like you and we&apos;ll take you to that pack.</p>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-494" data-mp="1u943j2zij53k">
        {recommendations.map(item => <li key={item.label} data-id="element-495" data-mp="1khggrfdwic8f">
            <a href={item.pack === 'business' ? '#company' : '#offer'} data-buy-intent="true" onClick={event => {
          trackCustom('pack_recommendation_selected', {
            choice: item.label,
            pack: item.pack
          });
          if (item.pack === 'business') {
            event.preventDefault();
            scrollToCompany();
            return;
          }
          requestOffer(item.pack);
        }} className="flex h-full flex-col border border-white/15 bg-ink-900 p-5 transition-[border-color,background-color] duration-150 ease-grit hover:border-grit-500 hover:bg-grit-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-496" data-mp="1kgewckazwde1">
              <span className="font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-497" data-mp="39q7gv22fe618">{item.label}</span>
              <span className="mt-1.5 text-[14px] text-steel-300" data-id="element-498" data-mp="3nqi8i7bk7ong">{item.body}</span>
              <span className="mt-auto flex items-center justify-between gap-2 pt-4 font-display text-lg font-bold uppercase text-grit-500" data-id="element-499" data-mp="3jon7l3xjjda7">
                {item.result}
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-500" data-mp="131uid1ksonuy" />
              </span>
            </a>
          </li>)}
      </ul>
    </>;
}
function CrewFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-501" data-mp="3rur0xtv6ao1x">
        One order.
        <br data-id="element-502" data-mp="laf2uokh7ko" />
        <span className="text-grit-500" data-id="element-503" data-mp="l1zgv127o5s">The whole crew.</span>
      </h2>
      <p className={subClass} data-id="element-504" data-mp="5kwpyxgxabqb">
        Every X1 ships as a complete system with Lifetime Replacement Warranty on every unit and free standard shipping.
      </p>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-505" data-mp="9kp5ju4qhi56">
        <a href="#offer" data-buy-intent="true" onClick={() => {
        requestOffer('crew');
        trackCustom('mini_funnel_cta_clicked', {
          pack: 'crew'
        });
      }} className="border border-white/15 bg-ink-900 p-6 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-506" data-mp="39381fx0cxs4n">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-507" data-mp="3mvefndjy9p1g">Buy 4, get 2 free</p>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-508" data-mp="2ud90xnlby4m0">6 X1 · $199.80</p>
          <p className="mt-1 text-[14px] text-steel-300" data-id="element-509" data-mp="2mthmq9c3ijdf">$33.30 per X1 · 18 lenses</p>
        </a>
        <a href="#company" data-buy-intent="true" onClick={event => {
        event.preventDefault();
        trackCustom('mini_funnel_cta_clicked', {
          pack: 'business'
        });
        scrollToCompany();
      }} className="border-2 border-grit-500 bg-grit-500/10 p-6 transition-colors duration-150 ease-grit hover:bg-grit-500/15" data-id="element-510" data-mp="2huwmwb0ra1pi">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-511" data-mp="1z45i7mrpqzhg">
            <BuildingIcon className="h-4 w-4" aria-hidden="true" data-id="element-512" data-mp="1u4khp7wwaj2m" />
            Business / crew pack
          </p>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-513" data-mp="1q4a1z3jaweak">30 X1 · $999.00</p>
          <p className="mt-1 text-[14px] text-steel-300" data-id="element-514" data-mp="1l3ckyua11qan">Buy 20, get 10 free · 90 lenses · one order</p>
        </a>
      </div>
    </>;
}
const closingCta: Partial<Record<ObjectionId, {
  pack: PackId;
  cta: string;
}>> = {
  battery: {
    pack: 'trio',
    cta: 'See multi-X1 options'
  },
  fit: {
    pack: 'single',
    cta: 'Choose your X1'
  },
  warranty: {
    pack: 'trio',
    cta: 'Choose your coverage'
  }
};

/**
 * One dynamic recovery area, hidden until the visitor tells us what is holding
 * them back. Only the matching answer renders, so the normal page stays clean.
 */
export function ObjectionRecovery({
  active,
  onChooseAnother
}: ObjectionRecoveryProps) {
  const choice = active ? objectionChoices.find(item => item.id === active) : undefined;
  useEffect(() => {
    if (choice) trackCustom('mini_funnel_viewed', {
      objection: choice.id
    });
  }, [choice]);
  const close = choice ? closingCta[choice.id] : undefined;
  return <AnimatePresence initial={false} data-id="element-515" data-mp="2gcy3wuwpqdm2">
      {choice && <motion.section id={RECOVERY_ID} key={choice.id} aria-labelledby="recovery-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-516" data-mp="22cdhlorl9znt">
          <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-517" data-mp="1xonk4mnlkjvg">
            <p className="inline-flex flex-wrap items-center gap-2 border border-grit-500/60 bg-grit-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-518" data-mp="1spavpxh2v4qr">
              You asked about:
              <span className="text-offwhite" data-id="element-519" data-mp="21qhhom2hx7re">{choice.label}</span>
            </p>

            <div id="recovery-heading" className="mt-6" data-id="element-520" data-mp="2gecae2gi7c4h">
              {choice.id === 'price' && <PriceFunnel data-id="element-521" data-mp="2bfiuy4hnpb1o" />}
              {choice.id === 'prescription' && <PrescriptionFunnel data-id="element-522" data-mp="2bg105qmaqzlq" />}
              {choice.id === 'battery' && <BatteryFunnel data-id="element-523" data-mp="2peil5gkthxzy" />}
              {choice.id === 'fit' && <FitFunnel data-id="element-524" data-mp="2pd7dh5o9463k" />}
              {choice.id === 'shipping' && <ShippingFunnel data-id="element-525" data-mp="2leq7wqkhb3o9" />}
              {choice.id === 'warranty' && <WarrantyFunnel data-id="element-526" data-mp="2xw6kj0c0gkdy" />}
              {choice.id === 'pack' && <PackRecommender data-id="element-527" data-mp="2xuvjv9dv3sr2" />}
              {choice.id === 'crew' && <CrewFunnel data-id="element-528" data-mp="2sxve6zkxcs0h" />}
            </div>

            {close && <PackClose recommended={close.pack} cta={close.cta} data-id="element-529" data-mp="2szg58f815stl" />}

            <button type="button" onClick={onChooseAnother} className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-530" data-mp="36vtptx7v2aca">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-531" data-mp="2nxz88uso2qia" />
              Something else? Choose another
            </button>
          </div>
        </motion.section>}
    </AnimatePresence>;
}